import { useGetCareTeam } from '~/lib/hooks/care-team/useGetCareTeam';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  Avatar,
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyIconButton,
  Tooltip,
} from '@equip.health/ui';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';
import { CarePlanInterventionResponse } from '~/lib/types/carePlan/carePlans';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { useState, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { EditInterventionAssigneesModal } from '~/components/maud/patients/profile/tabs/careplan/EditInterventionAssigneesModal';
import { useGetProviderTypeManifestQuery } from '~/store/services/refCarePlanEnums';
import {
  unwrapRoleGroups,
  mapRoleCodesToLabels,
} from '~/lib/util/carePlan.util';
import { EditCustomInterventionModal } from '~/components/maud/patients/profile/tabs/careplan/EditCustomInterventionModal';
import { VoidInterventionModal } from '~/components/maud/patients/profile/tabs/careplan/VoidInterventionModal';

type Props = {
  intervention: CarePlanInterventionResponse;
  isRowHovered: boolean;
  treatmentEpisodeId: string;
  isForPhaseOrStageClassification?: boolean;
};

export const CarePlanInterventionRowAvatarSection = ({
  intervention,
  isRowHovered,
  treatmentEpisodeId,
  isForPhaseOrStageClassification,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const { currentPatientId: patientId } = useGlobalContext();
  const { providers } = useGetCareTeam(patientId, true);
  const { carePlanM4CustomInterventions } = useFlags<LDFlags>();

  const [isEditingAssigneesModalOpen, setIsEditingAssigneesModalOpen] =
    useState(false);
  const [isEditCustomModalOpen, setIsEditCustomModalOpen] = useState(false);
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);

  const { data: providerTypeManifest } = useGetProviderTypeManifestQuery();

  // Combine direct roles + unwrapped role groups (using labels)
  const allRoles = useMemo(() => {
    // TODO NX-2562: Map role codes to labels for custom interventions only, remove once BE is fixed
    const roles =
      intervention.isCustom && providerTypeManifest
        ? mapRoleCodesToLabels(intervention.roles, providerTypeManifest)
        : [...intervention.roles];

    // If intervention has role groups, unwrap them and extract labels
    if (
      intervention.roleGroups &&
      intervention.roleGroups.length > 0 &&
      providerTypeManifest
    ) {
      const unwrappedProviderTypes = unwrapRoleGroups(
        intervention.roleGroups,
        providerTypeManifest,
      );
      roles.push(...unwrappedProviderTypes.map((pt) => pt.label));
    }

    // Remove duplicates and sort
    return [...new Set(roles)].sort((r1, r2) => r1.localeCompare(r2));
  }, [
    intervention.roles,
    intervention.roleGroups,
    intervention.isCustom,
    providerTypeManifest,
  ]);

  const handlePencilClick = () => {
    if (intervention.isCustom) {
      if (carePlanM4CustomInterventions) {
        setIsEditCustomModalOpen(true);
        return;
      }
    }
    setIsEditingAssigneesModalOpen(true);
  };

  let editAriaLabel = 'Edit intervention assignees';
  if (intervention.isCustom) {
    if (carePlanM4CustomInterventions) {
      editAriaLabel = 'Edit custom intervention';
    }
  }

  // For non-custom interventions with no roles, don't render anything
  if (allRoles.length === 0 && !intervention.isCustom) {
    return <></>;
  }

  return (
    <>
      <ChakraFlex alignItems="center" whiteSpace="nowrap">
        {allRoles.map((role) => {
          const providersAssignedRole = providers?.filter(
            (provider) => provider.role === role,
          );
          if (providersAssignedRole?.length > 0) {
            return providersAssignedRole.map((provider) => (
              <ChakraBox
                data-testid={`care-plan-intervention-${intervention.id}-provider-${provider.providerId}-role-${role}`}
                key={provider.providerId}
              >
                <ProviderAvatarTooltipWrapper
                  onAvatarHover={() =>
                    trackEvent(
                      isForPhaseOrStageClassification
                        ? CarePlanEvents.hoverOverInterventionAssigneeTargetInPhaseStageComponent
                        : CarePlanEvents.hoverOverInterventionAssigneeTargetInGoalCard,
                    )
                  }
                  placement="left"
                  providerExternalId={provider.providerId}
                >
                  <Avatar
                    name={`${provider.firstName} ${provider.lastName}`}
                    size="2xs"
                    src={provider.avatarSrc}
                    textSize="xs"
                    userType="provider"
                  />
                </ProviderAvatarTooltipWrapper>
              </ChakraBox>
            ));
          } else {
            return (
              <ChakraBox
                data-testid={`care-plan-intervention-${intervention.id}-role-${role}`}
                key={role}
              >
                <Tooltip
                  id={`care-plan-intervention-${intervention.id}-${role}`}
                  placement="left"
                  tooltipText={role}
                  width="110px"
                >
                  <Avatar
                    aria-label={`No one assigned for the ${role} role`}
                    size="2xs"
                    textSize="xs"
                    userType="provider"
                  />
                </Tooltip>
              </ChakraBox>
            );
          }
        })}
        <ChakraBox
          marginLeft="6px"
          visibility={isRowHovered ? undefined : 'hidden'}
        >
          <FireflyIconButton
            ariaLabel={editAriaLabel}
            icon={FAIconName.penRegular}
            id={`care-plan-intervention-edit-${intervention.id}`}
            onClick={handlePencilClick}
            size="small"
            variant="tertiary"
          />
        </ChakraBox>
      </ChakraFlex>

      {isEditingAssigneesModalOpen && (
        <EditInterventionAssigneesModal
          intervention={intervention}
          setIsEditingAssigneesModalOpen={setIsEditingAssigneesModalOpen}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}

      {isEditCustomModalOpen && (
        <EditCustomInterventionModal
          intervention={intervention}
          onClose={() => setIsEditCustomModalOpen(false)}
          onVoidClick={() => {
            setIsEditCustomModalOpen(false);
            setIsVoidModalOpen(true);
          }}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}

      {isVoidModalOpen && (
        <VoidInterventionModal
          intervention={intervention}
          isOpen
          onClose={() => setIsVoidModalOpen(false)}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}
    </>
  );
};
